import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Alert,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PictureAsPdf as PdfIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { formatStatus } from '../utils/formatStatus';
import { portalApi, InvoiceDetail as InvoiceDetailType } from '../services/portalApi';
import { useTranslation } from 'react-i18next';

export const InvoiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [invoice, setInvoice] = useState<InvoiceDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payingMethod, setPayingMethod] = useState<'card' | 'us_bank_account' | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      try {
        const data = await portalApi.getInvoiceDetail(id);
        setInvoice(data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(invoice?.locale || 'en-US', {
      style: 'currency',
      currency: invoice?.currencyCode || 'USD',
    }).format(amount);
  };

  const handlePay = async (method?: 'card' | 'us_bank_account') => {
    if (!invoice) return;
    setPayingMethod(method ?? 'card');
    try {
      const { checkoutUrl } = await portalApi.createPaymentSession(invoice.id, method);
      window.location.href = checkoutUrl;
    } catch (err: any) {
      setError(err.message || t('Failed to create payment session'));
      setPayingMethod(null);
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'processing':
        return 'info';
      case 'sent':
        return 'warning';
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/invoices')} sx={{ mb: 2 }}>
          {t('Back')}
        </Button>
        <Card>
          <CardContent>
            <Skeleton width="50%" height={32} />
            <Skeleton width="80%" height={24} />
            <Skeleton width="40%" height={24} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (error || !invoice) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/invoices')} sx={{ mb: 2 }}>
          {t('Back')}
        </Button>
        <Alert severity="error">{error || t('Invoice not found')}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/invoices')} sx={{ mb: 2 }}>
        {t('Back to Invoices')}
      </Button>

      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="h5">
                  {t('Invoice')} #{invoice.invoiceNumber}
                </Typography>
                <Chip
                  label={t(formatStatus(invoice.status))}
                  color={getStatusColor(invoice.status)}
                />
              </Box>

              <Typography color="text.secondary">
                {t('Issued')}: {formatDate(invoice.issueDate)}
                {invoice.dueDate && ` • ${t('Due Date')}: ${formatDate(invoice.dueDate)}`}
              </Typography>

              {invoice.periodStart && invoice.periodEnd && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('Service Period')}: {formatDate(invoice.periodStart)} - {formatDate(invoice.periodEnd)}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {invoice.pdfUrl && (
                <Button
                  variant="outlined"
                  startIcon={<PdfIcon />}
                  onClick={() => window.open(invoice.pdfUrl!, '_blank')}
                >
                  {t('Download PDF')}
                </Button>
              )}
              {invoice.amountDue > 0 && invoice.status !== 'void' && invoice.status !== 'processing' && (
                invoice.processingFeeEnabled && invoice.achEnabled ? (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<PaymentIcon />}
                      disabled={payingMethod !== null}
                      onClick={() => handlePay('us_bank_account')}
                    >
                      {payingMethod === 'us_bank_account'
                        ? t('Loading...')
                        : `${t('Pay by Bank Account')}${
                            invoice.achTotalWithFee !== undefined
                              ? ` (${formatCurrency(invoice.achTotalWithFee)})`
                              : ''
                          }`}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<PaymentIcon />}
                      disabled={payingMethod !== null}
                      onClick={() => handlePay('card')}
                    >
                      {payingMethod === 'card'
                        ? t('Loading...')
                        : `${t('Pay by Card')}${
                            invoice.cardTotalWithFee !== undefined
                              ? ` (${formatCurrency(invoice.cardTotalWithFee)})`
                              : ''
                          }`}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<PaymentIcon />}
                    disabled={payingMethod !== null}
                    onClick={() => handlePay()}
                  >
                    {payingMethod ? t('Loading...') : t('Pay Now')}
                  </Button>
                )
              )}
            </Box>
          </Box>

          {invoice.status === 'processing' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('Your bank payment is processing. This can take 3-5 business days to clear.')}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Line Items */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('Line Items')}</Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('Description')}</TableCell>
                <TableCell align="right">{t('Qty')}</TableCell>
                <TableCell align="right">{t('Unit Price')}</TableCell>
                <TableCell align="right">{t('Amount')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoice.lineItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {item.description}
                    {item.serviceDate && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {formatDate(item.serviceDate)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{item.quantity}</TableCell>
                  <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                  <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Divider sx={{ my: 2 }} />

          {/* Totals */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Typography>{t('Subtotal')}</Typography>
              <Typography sx={{ minWidth: 100, textAlign: 'right' }}>
                {formatCurrency(invoice.subtotal)}
              </Typography>
            </Box>
            {invoice.taxAmount > 0 &&
              (invoice.taxBreakdown?.length ? (
                invoice.taxBreakdown.map((tax, index) => (
                  <Box key={`${tax.name}-${index}`} sx={{ display: 'flex', gap: 4 }}>
                    <Typography>{tax.name} ({tax.rate}%){tax.pricesIncludeTax ? ' (included)' : ''}</Typography>
                    <Typography sx={{ minWidth: 100, textAlign: 'right' }}>
                      {formatCurrency(tax.taxAmount)}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Box sx={{ display: 'flex', gap: 4 }}>
                  <Typography>{t('Tax')}</Typography>
                  <Typography sx={{ minWidth: 100, textAlign: 'right' }}>
                    {formatCurrency(invoice.taxAmount)}
                  </Typography>
                </Box>
              ))}
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Typography sx={{ fontWeight: 600 }}>{t('Total')}</Typography>
              <Typography sx={{ minWidth: 100, textAlign: 'right', fontWeight: 600 }}>
                {formatCurrency(invoice.total)}
              </Typography>
            </Box>
            {invoice.amountPaid > 0 && (
              <Box sx={{ display: 'flex', gap: 4 }}>
                <Typography color="success.main">{t('Paid')}</Typography>
                <Typography sx={{ minWidth: 100, textAlign: 'right', color: 'success.main' }}>
                  -{formatCurrency(invoice.amountPaid)}
                </Typography>
              </Box>
            )}
            {invoice.status !== 'void' && invoice.amountDue > 0 && (
              <Box sx={{ display: 'flex', gap: 4 }}>
                <Typography sx={{ fontWeight: 600, color: 'warning.main' }}>{t('Balance Due')}</Typography>
                <Typography sx={{ minWidth: 100, textAlign: 'right', fontWeight: 600, color: 'warning.main' }}>
                  {formatCurrency(invoice.amountDue)}
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Payments */}
      {invoice.payments.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>{t('Payment History')}</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('Date')}</TableCell>
                  <TableCell>{t('Method')}</TableCell>
                  <TableCell align="right">{t('Amount')}</TableCell>
                  <TableCell>{t('Status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoice.payments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>{formatDate(payment.processedAt)}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{payment.method}</TableCell>
                    <TableCell align="right">{formatCurrency(payment.amount)}</TableCell>
                    <TableCell>
                      <Chip
                        label={t(formatStatus(payment.status))}
                        size="small"
                        color={payment.status === 'completed' ? 'success' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Notes */}
      {(invoice.notes || invoice.footerNotes) && (
        <Card>
          <CardContent>
            {invoice.notes && (
              <Box sx={{ mb: invoice.footerNotes ? 2 : 0 }}>
                <Typography variant="subtitle2" gutterBottom>{t('Notes')}</Typography>
                <Typography variant="body2">{invoice.notes}</Typography>
              </Box>
            )}
            {invoice.footerNotes && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>{t('Terms & Conditions')}</Typography>
                <Typography variant="body2" color="text.secondary">{invoice.footerNotes}</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
